// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

// Pure client-side average-rate computation for the EVOLVER PROGRESS panel
// (PRD §Interfaces "Client time-metric selector"; ADR-DRAFT-client-approx-time-metrics;
// issue `fe-dashboard-data-layer`, US §11/§17/§18).
// Averages over elapsed wall-clock since `started_at`, not instantaneous — debt: no
// per-iteration wall-clock timestamps on the wire yet; upgrade trigger when ebiose-core
// emits timestamped per-iteration counters (ADR-DRAFT-client-approx-time-metrics option A).
// Frozen at `ended_at` once the cycle is terminal so a finished cycle's rates stop
// drifting with the caller's `now`.

import { isFiniteNumber } from "./number-guards"

export interface TimeMetricsSnapshotTotals {
  totalCost: number | null | undefined
  totalCalls: number | null | undefined
  totalTokens: number | null | undefined
}

export interface TimeMetricsReport {
  burnPerMin: number | null
  // null unless both `budget` and a positive `burnPerMin` are known (AC2/AC3)
  budgetEtaMin: number | null
  callsPerMin: number | null
  tokensPerMin: number | null
}

const elapsedMinutesSince = (
  startedAt: string | null | undefined,
  now: number,
  endedAt: string | null | undefined,
): number | null => {
  if (startedAt == null) return null
  const startedMs = Date.parse(startedAt)
  if (!Number.isFinite(startedMs)) return null

  const endMs = endedAt != null ? Date.parse(endedAt) : now
  if (!Number.isFinite(endMs)) return null

  const elapsedMs = endMs - startedMs
  return elapsedMs > 0 ? elapsedMs / 60_000 : null
}

const perMinute = (
  total: number | null | undefined,
  elapsedMinutes: number | null,
): number | null =>
  elapsedMinutes != null && isFiniteNumber(total) ? total / elapsedMinutes : null

// Never throws; every edge case (missing `started_at`, missing totals, unknown budget)
// degrades to `null`, never `NaN`/`Infinity`/a fabricated `0`.
export const computeTimeMetrics = (
  totals: TimeMetricsSnapshotTotals | null | undefined,
  budget: number | null | undefined,
  startedAt: string | null | undefined,
  now: number,
  endedAt?: string | null,
): TimeMetricsReport => {
  const elapsedMinutes = elapsedMinutesSince(startedAt, now, endedAt)

  const burnPerMin = perMinute(totals?.totalCost, elapsedMinutes)
  const callsPerMin = perMinute(totals?.totalCalls, elapsedMinutes)
  const tokensPerMin = perMinute(totals?.totalTokens, elapsedMinutes)

  const totalCost = isFiniteNumber(totals?.totalCost) ? totals!.totalCost! : 0
  const budgetEtaMin =
    isFiniteNumber(budget) && burnPerMin != null && burnPerMin > 0
      ? (budget - totalCost) / burnPerMin
      : null

  return { burnPerMin, budgetEtaMin, callsPerMin, tokensPerMin }
}

export interface CompletionProgress {
  // Closed iterations, one more than the wire's last `iter_index`.
  completedIterations: number | null | undefined
  agentsEvaluated: number | null | undefined
  expectedPopulation: number | null | undefined
  maxIterations: number | null | undefined
}

// Every absent or degraded input degrades to `null`: a fabricated `0`, a `NaN`
// or an `Infinity` would reach the strip as a real projection (INV-3).
// debt: progress linear in agents evaluated and all iterations equally long, upgrade when the wire carries per-iteration wall-clock timestamps
// Same trigger as the averaging shortcut at the top of this module.
export const computeCompletionEtaMin = (
  progress: CompletionProgress,
  startedAt: string | null | undefined,
  now: number,
  endedAt?: string | null,
): number | null => {
  if (endedAt != null) return null

  const elapsedMinutes = elapsedMinutesSince(startedAt, now, null)
  if (elapsedMinutes == null) return null

  const { completedIterations, agentsEvaluated, expectedPopulation, maxIterations } =
    progress
  if (!isFiniteNumber(maxIterations) || maxIterations <= 0) return null
  if (!isFiniteNumber(expectedPopulation) || expectedPopulation <= 0) return null
  if (!isFiniteNumber(agentsEvaluated) || agentsEvaluated < 0) return null
  if (!isFiniteNumber(completedIterations) || completedIterations < 0) return null

  const evaluatedFraction = Math.min(agentsEvaluated / expectedPopulation, 1)
  // The two feeds collide at an iteration boundary: the snapshot has already
  // counted the iteration that closed while the progress row still carries its
  // full pair, so a full pair adds nothing once a closed iteration exists.
  const openIterationFraction =
    evaluatedFraction >= 1 && completedIterations > 0 ? 0 : evaluatedFraction
  const iterationsDone = completedIterations + openIterationFraction
  const iterationsLeft = maxIterations - iterationsDone
  if (iterationsDone <= 0 || iterationsLeft <= 0) return null

  return (elapsedMinutes / iterationsDone) * iterationsLeft
}
